import React, { useState, useCallback } from 'react';
import Layout from '../../components/layout';
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import unicorn from "./unicorn.jpg";
import { baseUrl } from '../../../Config/config';

const UnicornReportInner = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    designation: '',
    company_name: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!executeRecaptcha) {
      setError('reCAPTCHA not ready, please try again.');
      return;
    }

    setLoading(true);
    setError('');

    let recaptchaToken;
    try {
      recaptchaToken = await executeRecaptcha('unicorn_report');
    } catch {
      setLoading(false);
      setError('reCAPTCHA check failed. Please try again.');
      return;
    }

    try {
      const jsonData = {
        ...formData,
        recaptcha: recaptchaToken
      };

      const response = await fetch(`${baseUrl}/unicornReport`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(jsonData)
      });

      // On success the server streams the report PDF itself; every other
      // outcome (errors, restricted email) is JSON.
      const isPdf = (response.headers.get('Content-Type') || '').includes('application/pdf');

      if (response.ok && isPdf) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "Indian_Unicorn_Report.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);

        setSuccess(true);
        return;
      }

      const data = await response.json().catch(() => ({}));

      throw new Error(data.message || 'Failed to download the report');
      
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [executeRecaptcha, formData]);

  const formFields = [
    { name: 'name', type: 'text' },
    { name: 'email', type: 'email' },
    { name: 'designation', type: 'text' },
    { name: 'company_name', type: 'text', label: 'Company Name' },
    { name: 'phone', type: 'text' },
  ];

  return (
    <Layout>
      <div className="min-h-screen">
        <div className="relative h-80 mb-12">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url(${unicorn})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div className="absolute inset-0 bg-black/50"></div>
          </div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4"
          >
            <h1 className="text-5xl font-bold text-[#efefef] mb-6 relative group">
              <span className="relative">
                India Unicorn Report
                <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-[#c59c40] to-[#a07924] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
              </span>
            </h1>
            <h3 className="text-2xl md:text-[30px] text-[#efefef] font-semibold px-2 rounded-lg py-2">
              Complete the following details to access the report
            </h3>
          </motion.div>
        </div>

        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg py-8 px-6 mb-8"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                {formFields.map((field) => (
                  <div key={field.name} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 capitalize">
                      {field.label || field.name.replace('_', ' ')}*
                    </label>
                    <input
                      type={field.type}
                      name={field.name}
                      required
                      value={formData[field.name]}
                      onChange={handleChange}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#c59c40] focus:ring-[#c59c40] sm:text-sm px-4 py-2 border"
                    />
                  </div>
                ))}
              </div>

              {error && (
                <p className="text-red-600 text-sm text-center">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full max-w-md mx-auto flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#bc8b20] hover:bg-[#d6b15f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#c59c40] transition-colors duration-200"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Submit <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </button>

              {success && (
                <p className="text-green-600 text-sm text-center">
                  Downloaded successfully!
                </p>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

const UnicornReport = () => (
  <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY}>
    <UnicornReportInner />
  </GoogleReCaptchaProvider>
);

export default UnicornReport;